import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import {
  Palette, Check, Crown, Globe, Mail, Image, ExternalLink,
  RefreshCw, CreditCard, Settings, Users, ChevronRight, Zap, Shield, Star
} from 'lucide-react';
import { cn } from '../lib/utils';
import { PageInfoTooltip } from '../components/shared/PageInfoTooltip';

const API = process.env.REACT_APP_BACKEND_URL;

const PLAN_ICONS = { starter: Palette, professional: Crown, enterprise: Shield };
const PLAN_COLORS = {
  starter: 'border-emerald-500 bg-emerald-50',
  professional: 'border-blue-500 bg-blue-50',
  enterprise: 'border-purple-500 bg-purple-50',
};
const PLAN_BADGE_COLORS = {
  starter: 'bg-emerald-100 text-emerald-700',
  professional: 'bg-blue-100 text-blue-700',
  enterprise: 'bg-purple-100 text-purple-700',
};

// ─── Plan Card ───
const PlanCard = ({ plan, selected, onSelect }) => {
  const Icon = PLAN_ICONS[plan.id] || Palette;
  return (
    <Card
      className={cn(
        "cursor-pointer transition-all border-2 hover:shadow-md",
        selected ? PLAN_COLORS[plan.id] : "border-gray-border bg-white"
      )}
      onClick={() => onSelect(plan.id)}
      data-testid={`plan-card-${plan.id}`}
    >
      <CardContent className="p-4">
        <div className="flex items-center gap-3 mb-3">
          <div className={cn("w-10 h-10 rounded-lg flex items-center justify-center", selected ? "bg-white" : "bg-gray-100")}>
            <Icon className="h-5 w-5 text-gray-700" />
          </div>
          <div>
            <h3 className="font-heading font-semibold text-sm text-text-primary">{plan.name}</h3>
            <p className="text-lg font-bold text-text-primary">${plan.price}<span className="text-xs font-normal text-gray-medium">/mo</span></p>
          </div>
          {selected && <Check className="ml-auto h-5 w-5 text-emerald-600" />}
        </div>
        <ul className="space-y-1.5">
          {plan.features.map((f, i) => (
            <li key={i} className="flex items-start gap-2 text-xs text-gray-600">
              <Check className="h-3 w-3 text-emerald-500 mt-0.5 flex-shrink-0" />
              <span>{f}</span>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
};

// ─── Operator Row ───
const OperatorRow = ({ op, onConfigure, onSubscribe }) => (
  <div className="flex items-center gap-4 p-3 rounded-lg border border-gray-border bg-white hover:bg-off-white transition-colors" data-testid={`operator-row-${op.id}`}>
    <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
      <Users className="h-4 w-4 text-blue-600" />
    </div>
    <div className="flex-1 min-w-0">
      <p className="text-sm font-medium text-text-primary truncate">{op.full_name || op.email}</p>
      <p className="text-xs text-gray-medium truncate">{op.company || op.email}{op.country ? ` - ${op.country}` : ''}</p>
    </div>
    <div className="flex items-center gap-2 flex-shrink-0">
      {op.whitelabel_active ? (
        <>
          <Badge className={cn("text-[10px]", PLAN_BADGE_COLORS[op.plan] || "bg-gray-100 text-gray-700")}>
            {op.plan_name || 'Active'}
          </Badge>
          <Button variant="outline" size="sm" className="h-7 text-xs gap-1" onClick={() => onConfigure(op)} data-testid={`configure-btn-${op.id}`}>
            <Settings className="h-3 w-3" /> Configure
          </Button>
        </>
      ) : (
        <Button size="sm" className="h-7 text-xs bg-emerald-brand hover:bg-emerald-hover gap-1" onClick={() => onSubscribe(op)} data-testid={`subscribe-btn-${op.id}`}>
          <CreditCard className="h-3 w-3" /> Subscribe
        </Button>
      )}
    </div>
  </div>
);

// ─── Subscribe Dialog ───
const SubscribeDialog = ({ open, onOpenChange, operator, plans, onCheckout }) => {
  const [selectedPlan, setSelectedPlan] = useState('starter');
  const [loading, setLoading] = useState(false);

  const handleCheckout = async () => {
    setLoading(true);
    try {
      await onCheckout(operator.id, selectedPlan);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <CreditCard className="h-5 w-5 text-emerald-brand" />
            Subscribe: {operator?.full_name || operator?.email}
          </DialogTitle>
        </DialogHeader>
        <p className="text-sm text-gray-medium">Choose a monthly whitelabel plan for this Country Operator.</p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-3">
          {plans.map(p => (
            <PlanCard key={p.id} plan={p} selected={selectedPlan === p.id} onSelect={setSelectedPlan} />
          ))}
        </div>
        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button
            className="bg-emerald-brand hover:bg-emerald-hover gap-2"
            onClick={handleCheckout}
            disabled={loading}
            data-testid="checkout-btn"
          >
            {loading ? <RefreshCw className="h-4 w-4 animate-spin" /> : <CreditCard className="h-4 w-4" />}
            {loading ? 'Redirecting...' : `Pay $${plans.find(p => p.id === selectedPlan)?.price}/mo`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ─── Configure Dialog ───
const ConfigureDialog = ({ open, onOpenChange, operator, onSave }) => {
  const [config, setConfig] = useState({
    company_name: '', logo_url: '', favicon_url: '',
    primary_color: '#10B981', secondary_color: '#0F172A', accent_color: '#06B6D4',
    custom_domain: '', email_sender_name: '', login_heading: '', login_subheading: '',
    footer_text: '', hide_powered_by: false,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && operator) {
      setLoading(true);
      axios.get(`${API}/api/whitelabel/operators/${operator.id}/config`)
        .then(res => {
          const c = res.data.config || {};
          setConfig(prev => ({ ...prev, ...c }));
        })
        .catch(() => toast.error('Failed to load config'))
        .finally(() => setLoading(false));
    }
  }, [open, operator]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(operator.id, config);
      onOpenChange(false);
    } finally {
      setSaving(false);
    }
  };

  const updateField = (key, val) => setConfig(c => ({ ...c, [key]: val }));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <Palette className="h-5 w-5 text-emerald-brand" />
            Whitelabel Config: {operator?.full_name}
          </DialogTitle>
        </DialogHeader>
        {loading ? (
          <div className="flex items-center justify-center py-8"><RefreshCw className="h-6 w-6 animate-spin text-emerald-brand" /></div>
        ) : (
          <div className="space-y-4 mt-2">
            {/* Branding */}
            <div className="space-y-3">
              <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider flex items-center gap-2">
                <Image className="h-3 w-3" /> Branding
              </h4>
              <div>
                <Label className="text-xs">Company Name</Label>
                <Input value={config.company_name} onChange={e => updateField('company_name', e.target.value)} placeholder="e.g. Acme Corp" className="mt-1" data-testid="wl-company-name" />
              </div>
              <div>
                <Label className="text-xs">Logo URL</Label>
                <Input value={config.logo_url} onChange={e => updateField('logo_url', e.target.value)} placeholder="https://example.com/logo.png" className="mt-1" data-testid="wl-logo-url" />
              </div>
              <div>
                <Label className="text-xs">Favicon URL</Label>
                <Input value={config.favicon_url} onChange={e => updateField('favicon_url', e.target.value)} placeholder="https://example.com/favicon.ico" className="mt-1" />
              </div>
            </div>

            {/* Colors */}
            <div className="space-y-3">
              <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider flex items-center gap-2">
                <Palette className="h-3 w-3" /> Colors
              </h4>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label className="text-xs">Primary</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <input type="color" value={config.primary_color} onChange={e => updateField('primary_color', e.target.value)} className="w-8 h-8 rounded cursor-pointer border-0" />
                    <Input value={config.primary_color} onChange={e => updateField('primary_color', e.target.value)} className="text-xs h-8" />
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Secondary</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <input type="color" value={config.secondary_color} onChange={e => updateField('secondary_color', e.target.value)} className="w-8 h-8 rounded cursor-pointer border-0" />
                    <Input value={config.secondary_color} onChange={e => updateField('secondary_color', e.target.value)} className="text-xs h-8" />
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Accent</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <input type="color" value={config.accent_color} onChange={e => updateField('accent_color', e.target.value)} className="w-8 h-8 rounded cursor-pointer border-0" />
                    <Input value={config.accent_color} onChange={e => updateField('accent_color', e.target.value)} className="text-xs h-8" />
                  </div>
                </div>
              </div>
            </div>

            {/* Domain & Email */}
            <div className="space-y-3">
              <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider flex items-center gap-2">
                <Globe className="h-3 w-3" /> Domain & Email
              </h4>
              <div>
                <Label className="text-xs">Custom Domain (CNAME)</Label>
                <Input value={config.custom_domain} onChange={e => updateField('custom_domain', e.target.value)} placeholder="app.yourdomain.com" className="mt-1" data-testid="wl-custom-domain" />
              </div>
              <div>
                <Label className="text-xs">Email Sender Name</Label>
                <Input value={config.email_sender_name} onChange={e => updateField('email_sender_name', e.target.value)} placeholder="e.g. Acme Support" className="mt-1" />
              </div>
            </div>

            {/* Login Page */}
            <div className="space-y-3">
              <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider flex items-center gap-2">
                <ExternalLink className="h-3 w-3" /> Login Page
              </h4>
              <div>
                <Label className="text-xs">Login Heading</Label>
                <Input value={config.login_heading} onChange={e => updateField('login_heading', e.target.value)} placeholder="Welcome to Acme" className="mt-1" />
              </div>
              <div>
                <Label className="text-xs">Login Subheading</Label>
                <Input value={config.login_subheading} onChange={e => updateField('login_subheading', e.target.value)} placeholder="Sign in to your dashboard" className="mt-1" />
              </div>
              <div>
                <Label className="text-xs">Footer Text</Label>
                <Input value={config.footer_text} onChange={e => updateField('footer_text', e.target.value)} placeholder="2026 Acme Corp. All rights reserved." className="mt-1" />
              </div>
            </div>

            {/* Toggle */}
            <div className="flex items-center justify-between p-3 bg-off-white rounded-lg border border-gray-border">
              <div>
                <p className="text-sm font-medium text-text-primary">Hide "Powered by Konnect"</p>
                <p className="text-xs text-gray-medium">Remove Konnect branding from the footer</p>
              </div>
              <button
                onClick={() => updateField('hide_powered_by', !config.hide_powered_by)}
                className={cn(
                  "w-10 h-5 rounded-full transition-colors relative",
                  config.hide_powered_by ? "bg-emerald-brand" : "bg-gray-300"
                )}
                data-testid="wl-hide-powered-toggle"
              >
                <div className={cn(
                  "w-4 h-4 bg-white rounded-full absolute top-0.5 transition-transform shadow-sm",
                  config.hide_powered_by ? "translate-x-5" : "translate-x-0.5"
                )} />
              </button>
            </div>

            {/* Preview */}
            <div className="p-4 rounded-lg border-2 border-dashed border-gray-border">
              <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Preview</p>
              <div className="rounded-lg overflow-hidden border border-gray-200">
                <div className="h-10 flex items-center px-3 gap-2" style={{ backgroundColor: config.secondary_color }}>
                  {config.logo_url ? (
                    <img src={config.logo_url} alt="Logo" className="h-6 w-auto object-contain" onError={e => { e.target.style.display = 'none'; }} />
                  ) : (
                    <div className="h-6 w-16 rounded" style={{ backgroundColor: config.primary_color, opacity: 0.5 }} />
                  )}
                  <span className="text-white text-xs font-medium">{config.company_name || 'Company Name'}</span>
                </div>
                <div className="h-16 flex items-center justify-center" style={{ backgroundColor: config.primary_color + '15' }}>
                  <p className="text-xs" style={{ color: config.primary_color }}>{config.login_heading || 'Welcome'}</p>
                </div>
                {!config.hide_powered_by && (
                  <div className="h-6 bg-gray-50 flex items-center justify-center">
                    <p className="text-[9px] text-gray-400">Powered by Konnect</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button className="bg-emerald-brand hover:bg-emerald-hover gap-2" onClick={handleSave} disabled={saving} data-testid="save-config-btn">
            {saving ? 'Saving...' : 'Save Configuration'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};


// ─── Main Page ───
export default function WhitelabelPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [plans, setPlans] = useState([]);
  const [operators, setOperators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [subscribeOp, setSubscribeOp] = useState(null);
  const [configureOp, setConfigureOp] = useState(null);
  const [pollMsg, setPollMsg] = useState('');

  const fetchData = useCallback(async () => {
    try {
      const [plansRes, opsRes] = await Promise.all([
        axios.get(`${API}/api/whitelabel/plans`),
        axios.get(`${API}/api/whitelabel/operators`),
      ]);
      setPlans(plansRes.data.plans || []);
      setOperators(opsRes.data.operators || []);
    } catch (err) {
      if (err.response?.status === 403) {
        toast.error('Super Admin access required');
      } else {
        console.error(err);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Poll for payment status after Stripe redirect
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    const operatorId = searchParams.get('operator_id');
    if (!sessionId) return;

    let attempts = 0;
    const maxAttempts = 8;

    const poll = async () => {
      if (attempts >= maxAttempts) {
        setPollMsg('');
        toast.error('Payment status check timed out. Please refresh the page.');
        return;
      }
      setPollMsg('Verifying payment...');
      try {
        const res = await axios.get(`${API}/api/whitelabel/checkout/status/${sessionId}`);
        if (res.data.payment_status === 'paid') {
          setPollMsg('');
          toast.success('Whitelabel subscription activated!');
          searchParams.delete('session_id');
          searchParams.delete('operator_id');
          setSearchParams(searchParams, { replace: true });
          fetchData();
          return;
        }
      } catch {
        // continue polling
      }
      attempts++;
      setTimeout(poll, 2500);
    };

    poll();
  }, [searchParams, setSearchParams, fetchData]);

  const handleCheckout = async (operatorId, planId) => {
    try {
      const res = await axios.post(`${API}/api/whitelabel/checkout`, {
        plan_id: planId,
        operator_id: operatorId,
        origin_url: window.location.origin,
      });
      window.location.href = res.data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create checkout');
    }
  };

  const handleSaveConfig = async (operatorId, config) => {
    try {
      await axios.put(`${API}/api/whitelabel/operators/${operatorId}/config`, config);
      toast.success('Whitelabel configuration saved');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save config');
    }
  };

  const activeCount = operators.filter(o => o.whitelabel_active).length;
  const inactiveCount = operators.filter(o => !o.whitelabel_active).length;

  if (loading) {
    return <div className="flex items-center justify-center h-64"><RefreshCw className="h-8 w-8 animate-spin text-emerald-brand" /></div>;
  }

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="whitelabel-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-heading font-bold text-text-primary">
            Whitelabel Management
            <PageInfoTooltip pageKey="whitelabel-management" />
          </h1>
          <p className="text-gray-medium">Configure custom branding for Country Operators</p>
        </div>
        {pollMsg && (
          <Badge className="bg-amber-100 text-amber-700 gap-1 animate-pulse">
            <RefreshCw className="h-3 w-3 animate-spin" /> {pollMsg}
          </Badge>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Card className="bg-white border-gray-border">
          <CardContent className="p-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-medium">Total Operators</p>
                <p className="text-xl font-bold text-text-primary mt-1">{operators.length}</p>
              </div>
              <div className="w-9 h-9 rounded-lg bg-blue-100 flex items-center justify-center">
                <Users className="h-4 w-4 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white border-gray-border">
          <CardContent className="p-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-medium">Active Whitelabels</p>
                <p className="text-xl font-bold text-emerald-brand mt-1">{activeCount}</p>
              </div>
              <div className="w-9 h-9 rounded-lg bg-emerald-100 flex items-center justify-center">
                <Check className="h-4 w-4 text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white border-gray-border">
          <CardContent className="p-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-medium">Pending Setup</p>
                <p className="text-xl font-bold text-amber-600 mt-1">{inactiveCount}</p>
              </div>
              <div className="w-9 h-9 rounded-lg bg-amber-100 flex items-center justify-center">
                <Zap className="h-4 w-4 text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white border-gray-border">
          <CardContent className="p-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-medium">Monthly Revenue</p>
                <p className="text-xl font-bold text-text-primary mt-1">
                  ${operators.reduce((sum, o) => {
                    if (!o.whitelabel_active || !o.plan) return sum;
                    const p = plans.find(pl => pl.id === o.plan);
                    return sum + (p?.price || 0);
                  }, 0).toFixed(0)}
                </p>
              </div>
              <div className="w-9 h-9 rounded-lg bg-purple-100 flex items-center justify-center">
                <CreditCard className="h-4 w-4 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="operators" className="space-y-4">
        <TabsList className="overflow-x-auto flex-nowrap bg-white border border-gray-border w-full sm:w-auto">
          <TabsTrigger value="operators" className="text-xs sm:text-sm" data-testid="tab-operators">
            Country Operators ({operators.length})
          </TabsTrigger>
          <TabsTrigger value="plans" className="text-xs sm:text-sm" data-testid="tab-plans">
            Subscription Plans
          </TabsTrigger>
        </TabsList>

        {/* Operators Tab */}
        <TabsContent value="operators" className="space-y-3">
          {operators.length > 0 ? (
            operators.map(op => (
              <OperatorRow
                key={op.id}
                op={op}
                onConfigure={op => setConfigureOp(op)}
                onSubscribe={op => setSubscribeOp(op)}
              />
            ))
          ) : (
            <Card className="bg-white border-gray-border">
              <CardContent className="p-8 text-center">
                <Users className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                <p className="text-gray-medium">No Country Operators found</p>
                <p className="text-xs text-gray-400 mt-1">Create Country Operator accounts in the Admin Portal first.</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Plans Tab */}
        <TabsContent value="plans" className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {plans.map(p => {
              const Icon = PLAN_ICONS[p.id] || Palette;
              const subscriberCount = operators.filter(o => o.plan === p.id).length;
              return (
                <Card key={p.id} className={cn("border-2 bg-white", PLAN_COLORS[p.id]?.replace('bg-', 'bg-').split(' ')[0])} data-testid={`plan-detail-${p.id}`}>
                  <CardHeader className="pb-2">
                    <div className="flex items-center gap-2">
                      <Icon className="h-5 w-5 text-gray-700" />
                      <CardTitle className="text-base font-heading">{p.name}</CardTitle>
                    </div>
                    <p className="text-2xl font-bold text-text-primary mt-1">${p.price}<span className="text-sm font-normal text-gray-medium">/month</span></p>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2 mb-4">
                      {p.features.map((f, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm text-gray-600">
                          <Check className="h-4 w-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                          <span>{f}</span>
                        </li>
                      ))}
                    </ul>
                    <div className="pt-3 border-t border-gray-200">
                      <p className="text-xs text-gray-medium">
                        <span className="font-semibold text-text-primary">{subscriberCount}</span> active subscriber{subscriberCount !== 1 ? 's' : ''}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </TabsContent>
      </Tabs>

      {/* Dialogs */}
      {subscribeOp && (
        <SubscribeDialog
          open={!!subscribeOp}
          onOpenChange={v => { if (!v) setSubscribeOp(null); }}
          operator={subscribeOp}
          plans={plans}
          onCheckout={handleCheckout}
        />
      )}
      {configureOp && (
        <ConfigureDialog
          open={!!configureOp}
          onOpenChange={v => { if (!v) setConfigureOp(null); }}
          operator={configureOp}
          onSave={handleSaveConfig}
        />
      )}
    </div>
  );
}
